import { Grid, Stack } from '@careflux/primitives';
import { useState } from 'react';
import { BreakdownTable } from './BreakdownTable';
import { EditDistributionCard } from './EditDistributionCard';
import { MetricTile } from './MetricTile';
import { QueryState } from './QueryState';
import { TrendChart } from './TrendChart';
import type { EditRateOverviewProps, TabProps } from './components.types';
import { formatNumber, formatPercent } from './formatting';
import type { BreakdownDimension } from './types';
import { useSummaryBreakdown, useSummaryMetrics, useSummaryTimeline } from './useSummaryQualityQueries';

const editRateAxisDomain = ([min, max]: readonly [number, number]): [number, number] => {
  const padding = (max - min || max || 0.01) * 0.1;
  return [Math.max(0, min - padding), max + padding];
};

const editRateChartFormatter = new Intl.NumberFormat('is-IS', {
  style: 'percent',
  maximumFractionDigits: 4,
});

const EditRateOverview = ({ metrics }: EditRateOverviewProps) => (
  <Stack gap="lg">
    <Grid columns={{ base: 1, md: 2, xl: 4 }} gap="md">
      <MetricTile title="Samantektir" value={formatNumber(metrics.generations)} detail="Myndaðar á tímabilinu" />
      <MetricTile
        title="Mælingar"
        value={formatNumber(metrics.metricRows)}
        detail={`${formatNumber(metrics.pairedSaves)} paraðar · ${formatNumber(metrics.unpairedSaves)} óparaðar · ${formatNumber(metrics.unsaved)} óvistaðar`}
      />
      <MetricTile
        title="Meðalbreyting"
        value={formatPercent(metrics.editRateMean)}
        detail={`Miðgildi ${formatPercent(metrics.editRateMedian)}`}
        suppressed={metrics.suppressed}
      />
      <MetricTile
        title="Stafalíkindi"
        value={formatPercent(metrics.charSimilarityMean)}
        detail="AI útgáfa á móti vistaðri"
        suppressed={metrics.suppressed}
      />
    </Grid>
    <EditDistributionCard distribution={metrics.editDistribution} suppressed={metrics.suppressed} />
  </Stack>
);

export const EditRateTab = ({ filters, bucket, onBucketChange }: TabProps) => {
  const [by, setBy] = useState<BreakdownDimension>('model');
  const metricsQuery = useSummaryMetrics(filters);
  const timelineQuery = useSummaryTimeline(filters, bucket);
  const breakdownQuery = useSummaryBreakdown(filters, by);

  return (
    <Stack gap="lg">
      <QueryState
        isLoading={metricsQuery.isLoading}
        isError={metricsQuery.isError}
        error={metricsQuery.error}
        onRetry={() => void metricsQuery.refetch()}
        loadingLabel="Sæki yfirlit"
        errorTitle="Yfirlit hlóðst ekki"
      />
      {metricsQuery.data && <EditRateOverview metrics={metricsQuery.data} />}

      <QueryState
        isLoading={timelineQuery.isLoading}
        isError={timelineQuery.isError}
        error={timelineQuery.error}
        onRetry={() => void timelineQuery.refetch()}
        loadingLabel="Sæki tímalínu"
        errorTitle="Tímalína hlóðst ekki"
      />
      {timelineQuery.data && (
        <TrendChart
          title="Breytingahlutfall yfir tíma"
          description="Meðalhlutfall breyttra orða í pöruðum samantektum"
          points={timelineQuery.data}
          series={[{ dataKey: 'editRateMean', label: 'Meðalbreyting', color: 'hsl(var(--chart-1))' }]}
          formatValue={editRateChartFormatter.format}
          yAxisDomain={editRateAxisDomain}
          bucket={bucket}
          onBucketChange={onBucketChange}
          footnote="Tímabil með færri en 10 mælingum eru ekki sýnd."
        />
      )}

      <QueryState
        isLoading={breakdownQuery.isLoading}
        isError={breakdownQuery.isError}
        error={breakdownQuery.error}
        onRetry={() => void breakdownQuery.refetch()}
        loadingLabel="Sæki sundurliðun"
        errorTitle="Sundurliðun hlóðst ekki"
      />
      {breakdownQuery.data && <BreakdownTable rows={breakdownQuery.data} by={by} onByChange={setBy} />}
    </Stack>
  );
};
